/* 928sRus — event registration block (Third Coast and similar)
 *
 * Works with snippets/event-registration.liquid. Keeps a live total, enforces
 * per-registration limits and shared stock (e.g. 70 dinner seats across all
 * entrees), and adds the ticket plus every chosen extra to the cart in one call.
 *
 * Cart line properties:
 *   Event            visible, e.g. "Third Coast 2026"
 *   Driver           visible, the driver this registration is for (on every line)
 *   Guests in car    visible, on the ticket line only
 *   <choice label>   visible, e.g. "Entree: Steak" on dinner lines
 *   _event_uid       hidden, ties one registration's lines together
 *   _event_role      hidden, "Registration" or "Add-on"
 */
(function () {
  'use strict';

  function formatMoney(cents, format) {
    var value = (cents / 100).toFixed(2);
    var parts = value.split('.');
    var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/g, function (m, name) {
      if (name.indexOf('no_decimals') !== -1) return whole;
      return whole + '.' + parts[1];
    });
  }

  function toInt(value) {
    var n = parseInt(value, 10);
    return isNaN(n) || n < 0 ? 0 : n;
  }

  function EventRegistration(root) {
    this.root = root;
    this.eventName = root.getAttribute('data-event-name') || '';
    this.moneyFormat = root.getAttribute('data-money-format') || '${{amount}}';
    this.ticketEl = root.querySelector('[data-evreg-ticket]');
    this.guestsEl = root.querySelector('[data-evreg-guests]');
    this.driverEl = root.querySelector('[data-evreg-driver]');
    this.totalEl = root.querySelector('[data-evreg-total]');
    this.errorEl = root.querySelector('[data-evreg-error]');
    this.submitEl = root.querySelector('[data-evreg-submit]');
    this.submitting = false;

    this.bind();
    this.update();
    if (this.submitEl) this.submitEl.disabled = false;
  }

  EventRegistration.prototype.bind = function () {
    var self = this;
    this.root.addEventListener('input', function (e) {
      if (e.target.hasAttribute('data-evreg-qty')) self.enforceLimits(e.target);
      self.update();
    });
    this.root.addEventListener('change', function (e) {
      if (e.target.hasAttribute('data-evreg-qty')) self.enforceLimits(e.target);
      self.clearError();
      self.update();
    });
    if (this.submitEl) {
      this.submitEl.addEventListener('click', function () { self.submit(); });
    }
  };

  /* Keep one add-on section within its per-registration limit and stock. The
     box just edited gives way, so the other rows keep what the customer typed. */
  EventRegistration.prototype.enforceLimits = function (input) {
    var section = input.closest('[data-evreg-addon]');
    if (!section) return;
    var cap = toInt(section.getAttribute('data-limit')) || 10;
    var stockAttr = section.getAttribute('data-stock');
    if (stockAttr !== '' && stockAttr !== null) cap = Math.min(cap, toInt(stockAttr));

    var rowMax = toInt(input.getAttribute('max'));
    var value = Math.min(toInt(input.value), rowMax || cap);

    var others = 0;
    Array.prototype.forEach.call(section.querySelectorAll('[data-evreg-qty]'), function (el) {
      if (el !== input) others += toInt(el.value);
    });
    if (value + others > cap) value = Math.max(0, cap - others);

    if (String(value) !== input.value && input.value !== '') input.value = value;
  };

  EventRegistration.prototype.ticketChoice = function () {
    var el = this.ticketEl;
    if (!el) return null;
    if (el.tagName === 'SELECT') {
      var opt = el.options[el.selectedIndex];
      return { variant: opt ? opt.value : '', cents: toInt(opt && opt.getAttribute('data-cents')) };
    }
    return { variant: el.value, cents: toInt(el.getAttribute('data-cents')) };
  };

  EventRegistration.prototype.addonLines = function () {
    var lines = [];
    Array.prototype.forEach.call(this.root.querySelectorAll('[data-evreg-qty]'), function (el) {
      var qty = toInt(el.value);
      if (!qty) return;
      lines.push({
        variant: el.getAttribute('data-variant'),
        qty: qty,
        cents: toInt(el.getAttribute('data-cents')),
        propName: el.getAttribute('data-prop-name'),
        propValue: el.getAttribute('data-prop-value')
      });
    });
    return lines;
  };

  EventRegistration.prototype.update = function () {
    var ticket = this.ticketChoice();
    var total = ticket ? ticket.cents : 0;
    this.addonLines().forEach(function (l) { total += l.cents * l.qty; });
    if (this.totalEl) this.totalEl.textContent = formatMoney(total, this.moneyFormat);
  };

  EventRegistration.prototype.showError = function (msg) {
    if (!this.errorEl) return;
    this.errorEl.textContent = msg;
    this.errorEl.hidden = false;
  };

  EventRegistration.prototype.clearError = function () {
    if (this.errorEl) this.errorEl.hidden = true;
  };

  EventRegistration.prototype.submit = function () {
    if (this.submitting) return;
    this.clearError();

    var driver = this.driverEl ? this.driverEl.value.replace(/\s+/g, ' ').trim() : '';
    if (this.driverEl && driver.length < 2) {
      this.showError('Please enter the driver\'s name for this registration.');
      this.driverEl.focus();
      return;
    }

    var ticket = this.ticketChoice();
    if (!ticket || !ticket.variant) {
      this.showError('Please choose a T-shirt size for your free shirt.');
      if (this.ticketEl && this.ticketEl.focus) this.ticketEl.focus();
      return;
    }

    var self = this;
    var uid = 'ev-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
    var ticketProps = { Event: this.eventName, _event_uid: uid, _event_role: 'Registration' };
    if (driver) ticketProps.Driver = driver;
    if (this.guestsEl) ticketProps['Guests in car'] = String(toInt(this.guestsEl.value));

    var items = [{ id: Number(ticket.variant), quantity: 1, properties: ticketProps }];

    this.addonLines().forEach(function (l) {
      var props = { Event: self.eventName, _event_uid: uid, _event_role: 'Add-on' };
      if (driver) props.Driver = driver;
      if (l.propName && l.propValue) props[l.propName] = l.propValue;
      items.push({ id: Number(l.variant), quantity: l.qty, properties: props });
    });

    this.submitting = true;
    var btn = this.submitEl;
    var label = btn ? btn.innerHTML : '';
    if (btn) { btn.disabled = true; btn.innerHTML = 'Adding…'; }

    fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    })
      .then(function (res) {
        return res.json().then(function (data) {
          if (!res.ok) throw data;
          return data;
        });
      })
      .then(function () { window.location.href = '/cart'; })
      .catch(function (err) {
        self.submitting = false;
        if (btn) { btn.disabled = false; btn.innerHTML = label; }
        self.showError(
          (err && (err.description || err.message)) ||
            'Sorry, something went wrong adding your registration to the cart. Please try again or call 817-430-2688.'
        );
      });
  };

  function init() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-event-registration]'), function (root) {
      if (root.dataset.evregReady) return;
      root.dataset.evregReady = 'true';
      new EventRegistration(root);
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
